import './Signin.css';

import FormControl from '@mui/material/FormControl';
import React, { useState, useContext, ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import EmailInput from '../../../components/Inputs/EmailInput';
import PasswordInput from '../../../components/Inputs/PasswordInput';
import { Box, Container, Typography, CircularProgress } from '@mui/material';
import StyledButton from '../../../components/Button/Button';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import TopNav from '../../../components/TopNav/TopNav';
import { login } from '../../../services/NetworkServices';
import {
  getRole,
  storeApprovalStatus,
  storeEmail,
  storeStageNumber,
  storeToken,
} from '../../../utils/auth-token-utils/cookie';
import { SUPERVISOR_SESSION_MARKER } from '../../../hooks/useSupervisorAutoLogout';

import ApiStatus from '../../../config/apiStatusConstants';
import { LoginResponse } from '../../../services/types/APIResponseType';
import { AuthContext } from '../../../context/AuthContext';
import { LoginRequestPayload } from '../../../services/types/APIRequestType';

const Signin: React.FC = () => {
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('Auth Context must be used within a AuthProvider');
  }
  const { email, password, handleEmail, handlePassword, fcmToken } =
    authContext;
  const [errorMsg, setErrorMsg] = useState<string>('');

  const navigate = useNavigate();
  const role = getRole();

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    handleEmail(event.target.value);
  };

  const onChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
    handlePassword(event.target.value);
  };

  const handleSignin = async () => {
    setApiStatus(ApiStatus.LOADING);
    try {
      const payload: LoginRequestPayload = {
        email: email ?? '',
        password: password ?? '',
        role: role ?? '',
      };

      if (fcmToken) {
        payload.fcmToken = [fcmToken];
      }
      const response = await login(payload);

      const data: LoginResponse = await response.data;
      const stage = data.data.stage;
      const approvalStatus = data.data.approvalStatus;
      console.log('[Signin] raw response data:', data.data);
      console.log(
        '[Signin] stage:',
        stage,
        '| approvalStatus:',
        approvalStatus,
        '| role:',
        role,
      );
      storeStageNumber(stage);
      if (approvalStatus !== null && approvalStatus !== undefined) {
        storeApprovalStatus(approvalStatus);
      }
      console.log(
        '[Signin] stored approvalStatus in localStorage:',
        localStorage.getItem('approvalStatus'),
      );
      if (data) {
        storeToken(data.data.token);
        storeEmail(email);
        setApiStatus(ApiStatus.SUCCESS);
        if (role === 'Supervisor') {
          sessionStorage.setItem(SUPERVISOR_SESSION_MARKER, '1');
          const isApproved = !approvalStatus || approvalStatus === 'approved';
          console.log('[Signin] Supervisor isApproved:', isApproved);
          if (stage === '1') {
            console.log('[Signin] → navigating to /babysitter/createProfile');
            navigate('/babysitter/createProfile');
          } else if (!isApproved) {
            console.log(
              '[Signin] → navigating to /babysitter/waitingForApproval',
            );
            navigate('/babysitter/waitingForApproval');
          } else {
            console.log('[Signin] → navigating to /supervisor/dashboard');
            navigate('/supervisor/dashboard');
          }
        } else if (role === 'Participant') {
          if (stage === '1') {
            navigate('/participant/createProfile');
          }
          if (stage === '2') {
            navigate('/participant/inviteBabysitter');
          }
          if (stage === '3') {
            navigate('/participant/selectplan');
          }
          if (stage === '4') {
            navigate('/participant/dashboard');
          }
        }
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      setErrorMsg(
        error.response.data.message ||
          'Something went wrong. Please try again.',
      );
    }
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton>
          <CircularProgress size={20} />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton onClick={handleSignin} disabled={!(email && password)}>
          Sign In
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = () => {
    if (apiStatus === ApiStatus.FAILURE) {
      return <p className="error-msg">{errorMsg}</p>;
    }
    return null;
  };

  return (
    <StyledBgContainer>
      <TopNav />
      <Container maxWidth="sm">
        <Box component="form" noValidate autoComplete="off">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { sm: 6 },
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: 'white', fontFamily: 'Opensans', fontWeight: '700' }}
            >
              Sign In
            </Typography>
            <Typography variant="body2" sx={{ color: 'grey', mt: 1, mb: 3 }}>
              Provide the Email address and password entered during the Sign Up{' '}
              <br />
              process to access the web platform and mobile application.
            </Typography>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mb: 2, alignItems: 'center' }}
            >
              <EmailInput
                type="email"
                onChange={onChangeEmail}
                value={email}
                label="Email Address"
                placeholder="Enter your email"
                autoComplete="off"
              />
            </FormControl>
            {(errorMsg === 'Invalid email format' ||
              errorMsg.includes('User')) && (
              <Box sx={{ marginTop: '-20px', mb: 1 }}>{renderErrorMsg()}</Box>
            )}
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mb: 2, alignItems: 'center' }}
            >
              <PasswordInput
                onChange={onChangePassword}
                value={password}
                label="Enter Password"
              />
            </FormControl>

            <Box
              className="errorContainer"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {!(
                errorMsg === 'Invalid email format' || errorMsg.includes('User')
              ) && renderErrorMsg()}
              <Link to="/forgotpassword" className="forgotPassword">
                Forgot Password ?
              </Link>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 12 }}>{renderButton()}</Box>
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ color: 'grey', mt: 6, mb: 3 }}>
            Don't have an account ?{' '}
            <Link className="linkStyle" to="/signup">
              Sign Up
            </Link>
          </Typography>
        </Box>
      </Container>
    </StyledBgContainer>
  );
};

export default Signin;
